// @flow
import type { UseComboboxProps } from 'downshift';
import {
  useCombobox,
  useMultipleSelection,
  UseMultipleSelectionProps,
} from 'downshift';
import type { Item, ItemFetcher } from './item';
import _ from 'underscore';
import createAsyncFilter from './utils/create_async_filter';
import useAsyncDropdownFilter from './use_async_dropdown_filter';

const itemToString = (item) => item?.label || '';

export type UseMultiDropdownProps = UseComboboxProps<Item> & {
  items: Array<Item> | ItemFetcher,
  initialSelectedItems?: Array<Item>,
  creatable?: boolean,
  onSelectedItemsChange?: ({ selectedItems: Array<Item> }) => void | null,
  selectedItems?: Array<Item> | null,
};

// Downshift expects keys not to be undefined if they are set
function extractMultipleSelectionOptions(
  options
): UseMultipleSelectionProps<Item> {
  const multipleSelectionOptions: UseMultipleSelectionProps<Item> = {
    initialSelectedItems: options.initialSelectedItems || [],
  };
  if (options.initialSelectedItems) {
    multipleSelectionOptions.initialSelectedItems =
      options.initialSelectedItems;
  }
  if (options.onSelectedItemsChange) {
    multipleSelectionOptions.onSelectedItemsChange =
      options.onSelectedItemsChange;
  }
  return multipleSelectionOptions;
}

const useMultiDropdown = (options: UseMultiDropdownProps) => {
  const itemsFetcher = _.isFunction(options.items)
    ? options.items
    : createAsyncFilter(options.items);

  const useMultipleSelectionResult = useMultipleSelection(
    extractMultipleSelectionOptions(options)
  );

  const previouslyCreatedItems =
    useMultipleSelectionResult.selectedItems.filter((i) => i.userCreated);

  const [items, loadingStatus, onSearchTermChange] = useAsyncDropdownFilter(
    itemsFetcher,
    {
      creatable: options.creatable,
      previouslyCreatedItems,
    }
  );

  const selectableItems = items.filter((item) => {
    return !useMultipleSelectionResult.selectedItems
      .map((i) => i.value)
      .includes(item.value);
  });

  const useComboboxResult = useCombobox({
    defaultHighlightedIndex: 0, // after selection, highlight the first item.
    selectedItem: null,
    items: selectableItems,
    itemToString,
    stateReducer: (state, actionAndChanges) => {
      const { changes, type } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.InputBlur:
          return {
            ...changes,
            inputValue: '',
          };
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen: true, // keep the menu open after selection.
          };
      }
      return changes;
    },
    onStateChange: ({ inputValue, type, selectedItem }) => {
      switch (type) {
        case useCombobox.stateChangeTypes.InputChange:
          onSearchTermChange(inputValue);
          break;
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          if (selectedItem) {
            onSearchTermChange('');
            useMultipleSelectionResult.addSelectedItem(selectedItem);
          } else {
            if (_.isEmpty(items)) {
              onSearchTermChange('');
            }
          }
          break;
        default:
          break;
      }
    },
  });

  return {
    ...useComboboxResult,
    ...useMultipleSelectionResult,
    items: selectableItems,
    loadingStatus,
    loadInitialOptions: () => {
      onSearchTermChange('');
    },
  };
};

export default useMultiDropdown;
